/**
 * @file GenerateContent.tsx
 * @description Component for generating content.
 * Checks if user consent is required before allowing content generation.
 */

import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

import ContentForm from "../../components/common/ContentForm";
import PencilIcon from "../../components/common/icons/PencilIcon";
import log from "../../config/logging";
import { useNavbarContext } from "../../context/NavbarContext";
import { useUserDetails } from "../../context/UserDetailsContext";
import useGenerateContent from "../../hooks/useGenerateContent";
import { GenerateContentFormData } from "../../types";

interface GenerateContentProps {
  showHeader?: boolean;
}

const GenerateContent: React.FC<GenerateContentProps> = ({
  showHeader = true,
}) => {
  const {
    isAuthenticated,
    isUserConsentValid,
    refreshUserDetails,
    setShowConsentModal,
  } = useUserDetails();
  const { openLoginDropdown } = useNavbarContext();
  const [isLoading, setIsLoading] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [website, setWebsite] = useState("");
  const [contentType, setContentType] = useState("");
  const [clientInfo, setClientInfo] = useState("");
  const [relatedKeywords, setRelatedKeywords] = useState<string[]>([]);
  const [peopleAlsoAsk, setPeopleAlsoAsk] = useState<string[]>([]);
  const [title, setTitle] = useState("");
  const { generateContent } = useGenerateContent();

  useEffect(() => {
    log.info("GenerateContent component rendered");
  }, []);

  const handleSubmit = async (data: GenerateContentFormData) => {
    log.debug("Content generation requested", { data });
    if (!isAuthenticated) {
      log.debug("User not logged in, opening login dropdown");
      openLoginDropdown();
      return;
    }

    if (!isUserConsentValid()) {
      log.debug("User consent not valid, showing consent modal");
      setShowConsentModal(true);
      toast.info("Per favore, accetta i termini sulla privacy per continuare.");
      return;
    }

    try {
      setIsLoading(true);
      log.debug("Starting content generation");
      await generateContent(data);
      log.debug("Content generation completed successfully");
      await refreshUserDetails();
    } catch (error) {
      log.error("Error generating content", { error });
      toast.error(
        "Si è verificato un errore durante la generazione del contenuto. Riprova più tardi."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="mx-auto max-w-content">
      {showHeader && (
        <h2 className="!text-5xl !font-normal text-primary-content !mb-16 flex items-center gap-6">
          <PencilIcon className="w-11 h-11" />
          Genera il tuo nuovo contenuto SEO
        </h2>
      )}
      <div className="flex flex-col gap-4 sm:flex-row">
        <ContentForm
          keyword={keyword}
          setKeyword={setKeyword}
          website={website}
          setWebsite={setWebsite}
          contentType={contentType}
          setContentType={setContentType}
          clientInfo={clientInfo}
          setClientInfo={setClientInfo}
          relatedKeywords={relatedKeywords}
          setRelatedKeywords={setRelatedKeywords}
          peopleAlsoAsk={peopleAlsoAsk}
          setPeopleAlsoAsk={setPeopleAlsoAsk}
          title={title}
          setTitle={setTitle}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          variant="create"
        />
      </div>
    </section>
  );
};

export default GenerateContent;
